import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { ArrowLeft, Copy, FileCode, Lock, Save, Sparkles } from 'lucide-react';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField, FormGrid, FormSection } from '@/components/forms/FormSection';
import {
  addAgreementTemplateVersion,
  cloneAgreementTemplate,
  createAgreementTemplate,
  getAgreementTemplate,
  updateAgreementTemplate,
  type AgreementKind,
  type AgreementTemplateDetail,
  type AgreementTemplateVersionDetail,
} from '@/api/agreements';

/**
 * Template editor for the Legal & Agreements module.
 *
 * Routes:
 *   /agreements/templates/new          create-from-scratch
 *   /agreements/templates/:id/edit     edit existing (clones first if is_system)
 */

const KINDS: AgreementKind[] = ['MOU', 'NDA', 'PARTNERSHIP', 'SOW', 'DPA', 'OTHER'];

export function AgreementTemplateEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tpl, setTpl] = useState<AgreementTemplateDetail | null>(null);
  const [currentVersion, setCurrentVersion] = useState<AgreementTemplateVersionDetail | null>(null);

  const [name, setName] = useState('');
  const [kind, setKind] = useState<AgreementKind>('MOU');
  const [description, setDescription] = useState('');
  const [bodyXhtml, setBodyXhtml] = useState('');
  const [schemaJson, setSchemaJson] = useState('{}');
  const [changelog, setChangelog] = useState('');
  const [toast, setToast] = useState<string | null>(null);

  function notify(s: string) {
    setToast(s);
    setTimeout(() => setToast(null), 2500);
  }

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    setLoading(true);
    getAgreementTemplate(id!)
      .then((d) => {
        if (cancelled) return;
        setTpl(d);
        setName(d.name);
        setKind(d.kind);
        setDescription(d.description ?? '');
        const v = d.versions[0] ?? null;
        setCurrentVersion(v);
        setBodyXhtml(v?.bodyXhtml ?? '');
        setSchemaJson(v?.schema ? JSON.stringify(v.schema, null, 2) : '{}');
      })
      .catch((e) => {
        if (!cancelled) {
          if (isAxiosError(e) && e.response?.status === 404) {
            setError(t('agreements.templates.editor.notFound'));
          } else setError(e?.message ?? 'Failed to load');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id, isNew, t]);

  async function handleClone() {
    if (!tpl) return;
    setSaving(true);
    try {
      const cloned = await cloneAgreementTemplate(tpl.id);
      notify(t('agreements.templates.editor.cloned'));
      navigate(`/agreements/templates/${cloned.id}/edit`, { replace: true });
    } catch (e) {
      notify((e as Error).message);
    } finally { setSaving(false); }
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      // Validate JSON schema input
      try { JSON.parse(schemaJson); }
      catch { throw new Error(t('agreements.templates.editor.schemaInvalid')); }

      if (isNew) {
        const created = await createAgreementTemplate({
          name: name.trim(), kind, description: description.trim() || undefined,
          bodyXhtml, schemaJson,
        });
        notify(t('agreements.templates.editor.created'));
        navigate(`/agreements/templates/${created.id}/edit`, { replace: true });
        return;
      }

      if (!tpl) return;

      // Update metadata (name + description) if changed
      const metaChanged = name !== tpl.name || description !== (tpl.description ?? '');
      if (metaChanged && !tpl.isSystem) {
        await updateAgreementTemplate(tpl.id, {
          name: name.trim() !== tpl.name ? name.trim() : undefined,
          description: description.trim() !== (tpl.description ?? '') ? description.trim() : undefined,
        });
      }

      // Body / schema changed → new version
      const bodyChanged = bodyXhtml !== (currentVersion?.bodyXhtml ?? '');
      const schemaChanged = schemaJson !== (currentVersion?.schema ? JSON.stringify(currentVersion.schema, null, 2) : '{}');
      if (bodyChanged || schemaChanged) {
        if (tpl.isSystem) {
          throw new Error(t('agreements.templates.editor.systemNeedsClone'));
        }
        await addAgreementTemplateVersion(tpl.id, {
          bodyXhtml, schemaJson, changelog: changelog.trim() || undefined,
        });
      }

      notify(t('agreements.templates.editor.saved'));
      // Refresh
      const fresh = await getAgreementTemplate(tpl.id);
      setTpl(fresh);
      const v = fresh.versions[0] ?? null;
      setCurrentVersion(v);
      setChangelog('');
    } catch (err) {
      const detail = isAxiosError(err)
        ? (err.response?.data as { detail?: string } | undefined)?.detail
        : null;
      setError(detail ?? (err as Error).message);
    } finally { setSaving(false); }
  }

  if (loading) return <div className="p-8 text-sm text-muted-foreground">{t('common.loading')}</div>;

  return (
    <div className="space-y-4">
      <Breadcrumbs
        crumbs={[
          { label: t('agreements.nav.title'), to: '/agreements' },
          { label: t('agreements.nav.templates'), to: '/agreements/templates' },
          { label: isNew ? t('agreements.templates.editor.newTitle') : (tpl?.name ?? '') },
        ]}
      />

      {toast && <div className="fixed bottom-4 right-4 z-50 rounded-md border bg-card px-4 py-2 text-sm shadow-lg">{toast}</div>}

      <div className="rounded-lg border bg-card p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <FileCode className="h-6 w-6" />
              {isNew ? t('agreements.templates.editor.newTitle') : (tpl?.name ?? '—')}
            </h1>
            {tpl?.isSystem && (
              <p className="mt-1 text-sm text-amber-300 flex items-center gap-1">
                <Lock className="h-3 w-3" />
                {t('agreements.templates.editor.systemReadOnly')}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Link to="/agreements/templates">
              <Button size="sm" variant="outline">
                <ArrowLeft className="mr-1 h-3.5 w-3.5" />
                {t('common.back')}
              </Button>
            </Link>
            {tpl?.isSystem && (
              <Button size="sm" onClick={handleClone} disabled={saving}>
                <Copy className="mr-1 h-3.5 w-3.5" />
                {t('agreements.templates.editor.clone')}
              </Button>
            )}
          </div>
        </div>
      </div>

      <form onSubmit={handleSave} className="space-y-4">
        <FormSection title={t('agreements.templates.editor.metaSection')} defaultOpen>
          <FormGrid>
            <FormField label={t('agreements.templates.editor.field.name')} required>
              <Input value={name} onChange={(e) => setName(e.target.value)} required disabled={tpl?.isSystem} />
            </FormField>
            <FormField label={t('agreements.templates.editor.field.kind')}>
              <select
                value={kind}
                onChange={(e) => setKind(e.target.value as AgreementKind)}
                disabled={!isNew}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              >
                {KINDS.map((k) => <option key={k} value={k}>{t(`agreements.kind.${k}`)}</option>)}
              </select>
            </FormField>
          </FormGrid>
          <FormField label={t('agreements.templates.editor.field.description')}>
            <textarea
              value={description} onChange={(e) => setDescription(e.target.value)}
              disabled={tpl?.isSystem}
              rows={2}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            />
          </FormField>
        </FormSection>

        <FormSection title={t('agreements.templates.editor.bodySection')}
          subtitle={t('agreements.templates.editor.bodyHint')} defaultOpen>
          <FormField label={t('agreements.templates.editor.field.body')} hint={t('agreements.templates.editor.field.bodyHint')}>
            <textarea
              value={bodyXhtml} onChange={(e) => setBodyXhtml(e.target.value)}
              rows={20}
              disabled={tpl?.isSystem}
              className="w-full rounded-md border bg-background px-3 py-2 text-xs font-mono"
              placeholder='<article lang="es">…</article>'
            />
          </FormField>
        </FormSection>

        <FormSection title={t('agreements.templates.editor.schemaSection')}
          subtitle={t('agreements.templates.editor.schemaHint')} defaultOpen={false}>
          <FormField label={t('agreements.templates.editor.field.schema')}>
            <textarea
              value={schemaJson} onChange={(e) => setSchemaJson(e.target.value)}
              rows={14}
              disabled={tpl?.isSystem}
              className="w-full rounded-md border bg-background px-3 py-2 text-xs font-mono"
              placeholder='{"parties":{"type":"array",…}}'
            />
          </FormField>
        </FormSection>

        {!isNew && !tpl?.isSystem && (
          <FormSection title={t('agreements.templates.editor.changelogSection')} defaultOpen={false}>
            <FormField label={t('agreements.templates.editor.field.changelog')} hint={t('agreements.templates.editor.field.changelogHint')}>
              <Input value={changelog} onChange={(e) => setChangelog(e.target.value)} placeholder="Versión 2: revisión de clausula 7…" />
            </FormField>
          </FormSection>
        )}

        {tpl && (
          <div className="rounded-lg border bg-card p-3 text-xs text-muted-foreground">
            <Sparkles className="mr-1 inline h-3 w-3" />
            {t('agreements.templates.editor.versionStats', {
              count: tpl.versions.length,
              defaultValue: 'Versiones almacenadas: {{count}}',
            })}
          </div>
        )}

        {error && (
          <div className="rounded-md border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-300">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Link to="/agreements/templates">
            <Button type="button" variant="outline" size="sm">{t('common.cancel')}</Button>
          </Link>
          <Button type="submit" size="sm" disabled={saving || tpl?.isSystem || !name.trim()}>
            <Save className="mr-1 h-3.5 w-3.5" />
            {saving ? t('agreements.edit.saving') : t('agreements.edit.save')}
          </Button>
        </div>
      </form>
    </div>
  );
}
